import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Badge, Card, Empty, Modal, Select, Space, Table, Tag, Typography, type TableColumnsType } from 'antd'
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { sellerOrdersAPI, type SellerHeatmapLocationGroup, type SellerHeatmapPoint, type SellerHeatmapNearbyOrder } from '../utils/sellerApi'
import { formatSellerCurrency } from '../utils/currency'
import HeatLayer from '../components/HeatLayer'

const { Title, Text } = Typography

const metricOptions = [
  { label: 'Orders', value: 'orders' },
  { label: 'Items', value: 'items' },
  { label: 'Order value', value: 'value' },
]

const mapFrameStyle: CSSProperties = {
  height: 'clamp(460px, 62vh, 640px)',
  overflow: 'hidden',
  borderRadius: 24,
}

const heroAsideStyle: CSSProperties = {
  display: 'grid',
  gap: 12,
  justifyItems: 'end',
}

const MapResizeFix = () => {
  const map = useMap()

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      map.invalidateSize()
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [map])

  return null
}

const MapClickHandler = ({ onClick }: { onClick: (latitude: number, longitude: number) => void }) => {
  useMapEvents({
    click(event) {
      onClick(event.latlng.lat, event.latlng.lng)
    },
  })

  return null
}

const legendItems = [
  { label: 'Low', color: '#facc15' },
  { label: 'Medium', color: '#f97316' },
  { label: 'High', color: '#ef4444' },
]

type OrderRow = {
  key: number
  order_number: string
  created_at?: string | null
  total_amount: number
  items_count: number
}

const locationKey = (location: SellerHeatmapLocationGroup) =>
  `${location.pincode || location.postal_code || 'na'}|${location.city || 'na'}|${location.area || 'all'}`

const formatCompactNumber = (value: number) => new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 }).format(value)

const formatOrderDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : 'Unknown time')

const metricLabels: Record<string, string> = {
  orders: 'Orders',
  items: 'Items',
  value: 'Order value',
}

const kpiCardStyle: CSSProperties = {
  borderRadius: 20,
  border: '1px solid rgba(15, 23, 42, 0.08)',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(247,251,250,0.92) 100%)',
  boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08)',
}

const orderColumns: TableColumnsType<OrderRow> = [
  {
    title: 'Order',
    dataIndex: 'order_number',
    key: 'order_number',
    render: (value: string) => <Text strong>{value}</Text>,
  },
  {
    title: 'Placed At',
    dataIndex: 'created_at',
    key: 'created_at',
    render: (value?: string | null) => <Text type="secondary">{formatOrderDate(value)}</Text>,
  },
  {
    title: 'Amount',
    dataIndex: 'total_amount',
    key: 'total_amount',
    render: (value: number) => formatSellerCurrency(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  },
  {
    title: 'Items',
    dataIndex: 'items_count',
    key: 'items_count',
    render: (value: number) => <Badge count={value} style={{ backgroundColor: '#0f766e' }} />,
  },
]

type GroupOption = {
  value: string
  label: string
}

export const HeatmapPage = () => {
  const [metric, setMetric] = useState('orders')
  const [points, setPoints] = useState<SellerHeatmapPoint[]>([])
  const [locations, setLocations] = useState<SellerHeatmapLocationGroup[]>([])
  const [summary, setSummary] = useState<{ total_points: number; total_orders: number; total_value?: number; total_items?: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [pincodeFilter, setPincodeFilter] = useState('')
  const [cityFilter, setCityFilter] = useState('')
  const [selectedLocationKey, setSelectedLocationKey] = useState('')
  const [drilldownLoading, setDrilldownLoading] = useState(false)
  const [drilldownOpen, setDrilldownOpen] = useState(false)
  const [drilldownOrders, setDrilldownOrders] = useState<SellerHeatmapNearbyOrder[]>([])
  const [drilldownLocation, setDrilldownLocation] = useState<{ latitude: number; longitude: number } | null>(null)
  const [mapCenter, setMapCenter] = useState<[number, number] | null>([20.5937, 78.9629])
  const [mapZoom, setMapZoom] = useState<number>(4)

  const metricLabel = useMemo(
    () => metricOptions.find((item) => item.value === metric)?.label ?? metricLabels[metric] ?? 'Orders',
    [metric],
  )

  const groupedLocationOptions = useMemo<GroupOption[]>(() => {
    return locations.map((location) => {
      const labelParts = [location.pincode || location.postal_code || 'N/A', location.city || 'N/A']

      if (location.area) {
        labelParts.push(location.area)
      }

      return {
        value: locationKey(location),
        label: labelParts.join(' • '),
      }
    })
  }, [locations])
  const pincodeOptions = useMemo(() => {
    const source = cityFilter ? locations.filter((l) => ((l.city || '') || '').toLowerCase() === cityFilter.toLowerCase()) : locations
    return Array.from(new Set(source.map((location) => location.pincode || location.postal_code || '').filter(Boolean))).map((value) => ({ label: value, value }))
  }, [locations, cityFilter])

  const cityOptions = useMemo(() => {
    const source = pincodeFilter ? locations.filter((l) => (l.pincode || l.postal_code || '') === pincodeFilter) : locations
    return Array.from(new Set(source.map((location) => location.city || '').filter(Boolean))).map((value) => ({ label: value, value }))
  }, [locations, pincodeFilter])

  const handlePincodeChange = (value?: string) => {
    const v = value || ''
    setPincodeFilter(v)
    if (v) {
      const loc = locations.find((l) => (l.pincode || l.postal_code || '') === v)
      setCityFilter(loc?.city ?? '')
    }
  }

  const handleCityChange = (value?: string) => {
    const v = value || ''
    setCityFilter(v)
    if (v) {
      const pincodesInCity = new Set(locations.filter((l) => (l.city || '') === v).map((l) => l.pincode || l.postal_code || '').filter(Boolean))
      if (pincodeFilter && !pincodesInCity.has(pincodeFilter)) {
        setPincodeFilter('')
      }
    }
  }

  const selectedLocation = useMemo(
    () => locations.find((location) => locationKey(location) === selectedLocationKey) ?? locations[0] ?? null,
    [locations, selectedLocationKey],
  )

  const selectedOrders = useMemo<OrderRow[]>(() => {
    if (!selectedLocation) {
      return []
    }

    return selectedLocation.orders.map((order) => ({
      key: order.id,
      order_number: order.order_number || `Order #${order.id}`,
      created_at: order.created_at,
      total_amount: order.total_amount,
      items_count: order.items_count ?? 0,
    }))
  }, [selectedLocation])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const response = await sellerOrdersAPI.heatmap({
          metric,
          radius_meters: 2000,
          group: false,
          postal_code: pincodeFilter || undefined,
          city: cityFilter || undefined,
        })
        const payload = response.data?.data
        const pts = payload?.points ?? []
        const locs = payload?.locations ?? []
        setPoints(pts)
        setLocations(locs)
        setSummary(payload?.summary ?? null)

        // if filters applied, prefer centering on the location that matches the filters
        if ((pincodeFilter || cityFilter) && locs.length > 0) {
          let target: SellerHeatmapLocationGroup | undefined

          // if both filters present, look for an exact match first
          if (pincodeFilter && cityFilter) {
            target = locs.find(
              (l) => ((l.pincode || l.postal_code || '') === pincodeFilter) && ((l.city || '').toLowerCase() === cityFilter.toLowerCase()),
            )
          }

          // fallback to pincode match
          if (!target && pincodeFilter) {
            target = locs.find((l) => (l.pincode || l.postal_code || '') === pincodeFilter)
          }

          // fallback to city match
          if (!target && cityFilter) {
            target = locs.find((l) => (l.city || '').toLowerCase() === cityFilter.toLowerCase())
          }

          const first = target ?? locs[0]

          if (first && first.latitude && first.longitude) {
            setMapCenter([first.latitude, first.longitude])
            setMapZoom(12)
            // ensure the selected location UI follows the focused group
            try {
              setSelectedLocationKey(locationKey(first))
            } catch (e) {
              // ignore
            }
          }
        }
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Unable to load heatmap data.')
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [cityFilter, metric, pincodeFilter])

  useEffect(() => {
    if (groupedLocationOptions.length === 0) {
      setSelectedLocationKey('')
      return
    }

    const selectedStillExists = groupedLocationOptions.some((option) => option.value === selectedLocationKey)

    if (!selectedStillExists) {
      setSelectedLocationKey(groupedLocationOptions[0].value)
    }
  }, [groupedLocationOptions, selectedLocationKey])

  const handleMapClick = async (latitude: number, longitude: number) => {
    setDrilldownLoading(true)
    setDrilldownOpen(true)
    setDrilldownLocation({ latitude, longitude })

    try {
      const response = await sellerOrdersAPI.nearby({ latitude, longitude, radius_meters: 2000 })
      setDrilldownOrders(response.data?.data ?? [])
    } catch {
      setDrilldownOrders([])
    } finally {
      setDrilldownLoading(false)
    }
  }

  const MapAutoCenter = ({ center, zoom }: { center: [number, number] | null; zoom?: number }) => {
    const map = useMap()

    useEffect(() => {
      if (center && map) {
        try {
          map.flyTo(center, zoom ?? Math.max(map.getZoom(), 10), { duration: 0.6 })
        } catch (e) {
          // ignore
        }
      }
    }, [center, zoom, map])

    return null
  }

  return (
    <div className="seller-heatmap-page">
      <Card className="seller-heatmap-hero" variant="borderless">
        <div className="seller-heatmap-hero__inner">
          <Space orientation="vertical" size={8}>
            <Tag color="teal" className="seller-heatmap-hero__eyebrow">Order distribution</Tag>
            <Title level={2} style={{ margin: 0 }}>Heatmap</Title>
            <Text type="secondary" className="seller-heatmap-hero__copy">
              See where delivered orders cluster, then drill into one pincode and city at a time.
            </Text>
          </Space>

          <div style={heroAsideStyle}>
            <Select
              options={metricOptions}
              value={metric}
              style={{ minWidth: 190, textAlign: 'left' }}
              onChange={(value) => setMetric(value)}
            />
            <Text type="secondary">
              Showing {metricLabel.toLowerCase()} across {groupedLocationOptions.length} location groups.
            </Text>
          </div>
        </div>

        <div className="seller-heatmap-kpis">
          <Card className="seller-heatmap-kpi" style={kpiCardStyle} variant="borderless">
            <Text type="secondary">Points</Text>
            <Title level={3} style={{ margin: '6px 0 0' }}>{formatCompactNumber(summary?.total_points ?? points.length)}</Title>
            <Text type="secondary">Heat locations currently plotted</Text>
          </Card>
          <Card className="seller-heatmap-kpi" style={kpiCardStyle} variant="borderless">
            <Text type="secondary">Orders</Text>
            <Title level={3} style={{ margin: '6px 0 0' }}>{formatCompactNumber(summary?.total_orders ?? 0)}</Title>
            <Text type="secondary">Delivered orders in the current view</Text>
          </Card>
          <Card className="seller-heatmap-kpi" style={kpiCardStyle} variant="borderless">
            <Text type="secondary">Value</Text>
            <Title level={3} style={{ margin: '6px 0 0' }}>{formatSellerCurrency(summary?.total_value ?? 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Title>
            <Text type="secondary">Aggregate order value</Text>
          </Card>
          <Card className="seller-heatmap-kpi" style={kpiCardStyle} variant="borderless">
            <Text type="secondary">Groups</Text>
            <Title level={3} style={{ margin: '6px 0 0' }}>{formatCompactNumber(groupedLocationOptions.length)}</Title>
            <Text type="secondary">Pincode / city buckets after filters</Text>
          </Card>
        </div>

        <div className="seller-heatmap-filters">
          <Select
            showSearch
            allowClear
            placeholder="Filter by pincode"
            className="seller-heatmap-filter"
            options={pincodeOptions}
            value={pincodeFilter || undefined}
            optionFilterProp="label"
            onChange={(value) => handlePincodeChange(value)}
            onClear={() => handlePincodeChange('')}
          />
          <Select
            showSearch
            allowClear
            placeholder="Filter by city"
            className="seller-heatmap-filter"
            options={cityOptions}
            value={cityFilter || undefined}
            optionFilterProp="label"
            onChange={(value) => handleCityChange(value)}
            onClear={() => handleCityChange('')}
          />
          <div className="seller-heatmap-legend">
            {legendItems.map((item) => (
              <Tag key={item.label} className="seller-heatmap-legend__item">
                <span className="seller-heatmap-legend__dot" style={{ background: item.color }} />
                {item.label}
              </Tag>
            ))}
          </div>
        </div>
      </Card>

      {loading ? (
        <Card loading className="seller-heatmap-surface" style={{ minHeight: 320 }} variant="borderless" />
      ) : error ? (
        <Empty description={error} />
      ) : (
        <div className="seller-heatmap-grid">
          <Card
            className="seller-heatmap-surface seller-heatmap-map-card"
            title={`Distribution by ${metricLabel.toLowerCase()}`}
            variant="borderless"
          >
            <div style={mapFrameStyle} className="seller-heatmap-map-frame">
              <MapContainer center={[20.5937, 78.9629]} zoom={4} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  attribution='&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; OpenStreetMap contributors'
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                />
                <MapResizeFix />
                {mapCenter ? <MapAutoCenter center={mapCenter} zoom={mapZoom} /> : null}
                {points.length > 0 ? <HeatLayer points={points} radius={25} blur={15} maxZoom={12} /> : null}
                <MapClickHandler onClick={handleMapClick} />
              </MapContainer>
            </div>
          </Card>

          <Card className="seller-heatmap-surface seller-heatmap-location-card" title="Location groups" variant="borderless">
            <Space orientation="vertical" size={14} style={{ width: '100%' }}>
              <div className="seller-heatmap-location-card__header">
                <Select
                  showSearch
                  placeholder="Choose pincode / city / area"
                  className="seller-heatmap-location-select"
                  value={selectedLocation ? locationKey(selectedLocation) : undefined}
                  options={groupedLocationOptions}
                  optionFilterProp="label"
                  onChange={(value) => setSelectedLocationKey(value)}
                />

                {selectedLocation ? (
                  <Space wrap className="seller-heatmap-location-card__metrics">
                    <Card size="small" className="seller-heatmap-mini-metric" variant="borderless">
                      <Text type="secondary">Selected orders</Text>
                      <Title level={4} style={{ margin: '4px 0 0' }}>{formatCompactNumber(selectedLocation.count)}</Title>
                    </Card>
                    <Card size="small" className="seller-heatmap-mini-metric" variant="borderless">
                      <Text type="secondary">Value</Text>
                      <Title level={4} style={{ margin: '4px 0 0' }}>{formatSellerCurrency(selectedLocation.total_value ?? 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Title>
                    </Card>
                  </Space>
                ) : null}
              </div>

              {selectedLocation ? (
                <div className="seller-heatmap-location-card__body">
                  <Space wrap className="seller-heatmap-location-meta">
                    <Tag color="cyan">Pincode: {selectedLocation.pincode || selectedLocation.postal_code || 'N/A'}</Tag>
                    <Tag color="geekblue">City: {selectedLocation.city || 'N/A'}</Tag>
                    {selectedLocation.area ? <Tag color="gold">Area: {selectedLocation.area}</Tag> : null}
                    <Tag>First order: {selectedLocation.first_order_at ? formatOrderDate(selectedLocation.first_order_at) : 'N/A'}</Tag>
                    <Tag>Last order: {selectedLocation.last_order_at ? formatOrderDate(selectedLocation.last_order_at) : 'N/A'}</Tag>
                  </Space>

                  <Table<OrderRow>
                    rowKey="key"
                    columns={orderColumns}
                    dataSource={selectedOrders}
                    pagination={{ pageSize: 8, showSizeChanger: false }}
                    size="middle"
                    className="seller-heatmap-table"
                  />
                </div>
              ) : (
                <Empty description="No location group selected" />
              )}
            </Space>
          </Card>
        </div>
      )}

      <Modal
        title={drilldownLocation ? `Orders near ${drilldownLocation.latitude.toFixed(5)}, ${drilldownLocation.longitude.toFixed(5)}` : 'Orders nearby'}
        open={drilldownOpen}
        onCancel={() => setDrilldownOpen(false)}
        footer={null}
        width={720}
      >
        {drilldownLoading ? (
          <span>Loading nearby orders...</span>
        ) : drilldownOrders.length === 0 ? (
          <Empty description="No orders inside this area" />
        ) : (
          <Space orientation="vertical" style={{ width: '100%' }} size={8}>
            {drilldownOrders.map((order) => (
              <Card key={order.id} size="small">
                <Space orientation="vertical" size={2}>
                  <strong>{order.order_number}</strong>
                  <span>Orders value: {order.total_amount}</span>
                  <span>Items: {order.items_count ?? 0}</span>
                </Space>
              </Card>
            ))}
          </Space>
        )}
      </Modal>
    </div>
  )
}

export default HeatmapPage
